import ApplicationController from "./application_controller";

// Gefuehrter Dokumenten-Upload im Modal (Bewerbungsprozess, Schritt
// "Dokumente"): 1) Datei waehlen 2) Dokumentart waehlen 3) Hochladen.
// "Weiter" ist erst aktiv, wenn der aktuelle Schritt erledigt ist - so kann
// weder die Dokumentart noch der Klick auf "Hochladen" uebersehen werden.
// Nach der Dateiwahl wird die Dokumentart vorausgewaehlt, wenn sie erkennbar ist:
// Bilder -> Foto-Kategorie, sonst Suchbegriffe der Kategorie im Dateinamen
// (laengster Treffer gewinnt). PDF passt zu allen Kategorien (auch Foto), Bilder
// (JPG/PNG) nur zu Foto-Kategorien. Die Auswahl bleibt in Schritt 2 aenderbar.
export default class extends ApplicationController {
    static targets = [
        'modal', 'step', 'indicator', 'input', 'dropzone', 'fileName', 'error',
        'category', 'categoryCard', 'detected', 'summaryFile', 'summaryType',
        'backButton', 'cancelButton', 'nextButton', 'submitButton'
    ]

    static values = {
        maxSize: { type: Number, default: 10 },
        tooSmall: String,
        tooLarge: String,
        pdfOnly: String,
        pdfOrPhoto: String,
        photoAllowed: Boolean,
        noFile: String
    }

    connect() {
        this.current = 0;
        this.render();
    }

    open() {
        this.modalTarget.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
        this.render();
    }

    close() {
        this.modalTarget.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
    }

    closeOnBackdrop(event) {
        if (event.target === this.modalTarget) this.close();
    }

    closeOnEscape(event) {
        if (event.key === 'Escape' && !this.modalTarget.classList.contains('hidden')) this.close();
    }

    next() {
        if (!this.stepComplete(this.current)) return;
        this.current = Math.min(this.current + 1, this.stepTargets.length - 1);
        this.render();
    }

    back() {
        this.current = Math.max(this.current - 1, 0);
        this.render();
    }

    // --- Schritt 1: Datei -------------------------------------------------

    fileChosen() {
        const file = this.inputTarget.files[0];
        const error = this.validate(file);

        if (error) {
            this.inputTarget.value = '';
            this.showError(error);
            this.restrictCategories(null);
        } else {
            this.hideError();
            if (file) this.preselectCategory(file);
        }
        this.render();
    }

    fileKind(file) {
        const name = file.name.toLowerCase();
        if (file.type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
        if (['image/jpeg', 'image/png'].includes(file.type) || /\.(jpe?g|png)$/.test(name)) return 'image';
        return null;
    }

    // Bei Bildern nur Kategorien anbieten, die Bilder annehmen; PDF und null = alle.
    restrictCategories(kind) {
        this.categoryTargets.forEach((radio) => {
            const allowed = kind !== 'image' || radio.dataset.kind === 'image';
            radio.disabled = !allowed;
            if (!allowed) radio.checked = false;
            radio.closest('[data-document-upload-wizard-target="categoryCard"]').classList.toggle('opacity-40', !allowed);
        });
    }

    preselectCategory(file) {
        const kind = this.fileKind(file);
        this.restrictCategories(kind);

        const candidates = this.categoryTargets.filter((radio) => !radio.disabled);
        const detected = kind === 'image' && candidates.length === 1 ? candidates[0] : this.matchByFilename(file.name, candidates);
        if (detected) detected.checked = true;
        this.detected = !!detected;
    }

    matchByFilename(filename, candidates) {
        const name = filename.toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[^a-z0-9äöüß]+/g, ' ');
        let best = null;
        let bestLength = 0;
        candidates.forEach((radio) => {
            (radio.dataset.keywords || '').split('|').filter(Boolean).forEach((keyword) => {
                // "cv" nur als eigenes Wort, "cover"/"vita" nur am Wortanfang (nicht in
                // "Recovery"), laengere Begriffe auch innerhalb ("Arbeitszeugnis").
                let hit;
                if (keyword.length <= 3) hit = new RegExp(`(^| )${keyword}( |$)`).test(name);
                else if (keyword.length <= 5) hit = new RegExp(`(^| )${keyword}`).test(name);
                else hit = name.includes(keyword);
                if (hit && keyword.length > bestLength) {
                    best = radio;
                    bestLength = keyword.length;
                }
            });
        });
        return best;
    }

    dragOver(event) {
        event.preventDefault();
        this.dropzoneTarget.classList.add('border-primary', 'bg-gray-50');
    }

    dragLeave() {
        this.dropzoneTarget.classList.remove('border-primary', 'bg-gray-50');
    }

    drop(event) {
        event.preventDefault();
        this.dragLeave();
        if (!event.dataTransfer || event.dataTransfer.files.length === 0) return;

        this.inputTarget.files = event.dataTransfer.files;
        this.fileChosen();
    }

    validate(file) {
        if (!file) return null;
        const kind = this.fileKind(file);
        if (!kind || (kind === 'image' && !this.photoAllowedValue)) {
            return this.photoAllowedValue ? this.pdfOrPhotoValue : this.pdfOnlyValue;
        }
        if (file.size < 1024) return this.tooSmallValue;
        if (file.size > this.maxSizeValue * 1024 * 1024) return this.tooLargeValue;
        return null;
    }

    showError(message) {
        this.errorTarget.textContent = message;
        this.errorTarget.classList.remove('hidden');
    }

    hideError() {
        this.errorTarget.classList.add('hidden');
    }

    // --- Schritt 2: Dokumentart ------------------------------------------

    categoryChosen() {
        this.detected = false;
        this.render();
    }

    selectedCategory() {
        return this.categoryTargets.find((radio) => radio.checked);
    }

    // --- Schritt 3: Hochladen --------------------------------------------

    submit(event) {
        if (!this.stepComplete(0) || !this.stepComplete(1)) {
            event.preventDefault();
            return;
        }
        // Doppelklick verhindern, waehrend die Datei hochgeladen wird.
        this.submitButtonTarget.disabled = true;
        this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.uploadingText;
    }

    // --- Darstellung -------------------------------------------------------

    stepComplete(index) {
        if (index === 0) return this.inputTarget.files.length > 0;
        if (index === 1) return !!this.selectedCategory();
        return true;
    }

    render() {
        const last = this.stepTargets.length - 1;
        const file = this.inputTarget.files[0];
        const category = this.selectedCategory();

        this.stepTargets.forEach((step, index) => step.classList.toggle('hidden', index !== this.current));

        this.indicatorTargets.forEach((indicator, index) => {
            const number = indicator.querySelector('[data-role="number"]');
            const label = indicator.querySelector('[data-role="label"]');
            const reached = index <= this.current;
            number.classList.toggle('bg-primary', reached);
            number.classList.toggle('text-white', reached);
            number.classList.toggle('bg-gray-200', !reached);
            number.classList.toggle('text-gray-600', !reached);
            label.classList.toggle('text-gray-900', index === this.current);
            label.classList.toggle('text-gray-500', index !== this.current);
        });

        this.categoryCardTargets.forEach((card) => {
            const checked = card.querySelector('input').checked;
            card.classList.toggle('border-primary', checked);
            card.classList.toggle('ring-2', checked);
            card.classList.toggle('ring-primary', checked);
        });

        this.detectedTarget.classList.toggle('hidden', !(this.detected && category));
        this.fileNameTarget.textContent = file ? file.name : this.noFileValue;
        this.summaryFileTarget.textContent = file ? file.name : '';
        this.summaryTypeTarget.textContent = category ? category.dataset.title : '';

        this.backButtonTarget.classList.toggle('hidden', this.current === 0);
        this.cancelButtonTarget.classList.toggle('hidden', this.current !== 0);
        this.nextButtonTarget.classList.toggle('hidden', this.current === last);
        this.nextButtonTarget.disabled = !this.stepComplete(this.current);
        this.submitButtonTarget.classList.toggle('hidden', this.current !== last);
    }
}
